import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { swedishMunicipalities } from '../data/municipalities';

interface FormData {
  gatuadress: string;
  kommun: string;
  fastighetsbeteckning: string;
  husetHarKallare: string;
  vaAnslutning: string;
  backventil: string;
  lagstaNiva: string;
  golvbrunnToalettHandfat: string;
  email: string;
}

const FormPage: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<FormData>({
    gatuadress: '',
    kommun: '',
    fastighetsbeteckning: '',
    husetHarKallare: '',
    vaAnslutning: '',
    backventil: '',
    lagstaNiva: '',
    golvbrunnToalettHandfat: '',
    email: ''
  });

  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [municipalities, setMunicipalities] = useState<string[]>([]);
  const [loadingMunicipalities, setLoadingMunicipalities] = useState(false);

  const helpTexts = {
    gatuadress: 'Ange den fullständiga gatuadressen inklusive gatunummer, t.ex. "Storgatan 15"',
    kommun: 'Välj den kommun där fastigheten är belägen',
    fastighetsbeteckning: 'Fastighetsbeteckningen hittar du på fastighetsutdraget eller taxeringsintyget, t.ex. "Götene Skagen 5:12" Du kan även hitta din fastighetsbeteckning om du loggar in på "Min Fastighet" på Lantmäteriets hemsida',
    husetHarKallare: 'Ange om fastigheten har källare och om den är inredd eller oinredd',
    vaAnslutning: 'Typ av VA-anslutning: Enskilt (egen anläggning), Separat (separata ledningar för vatten/avlopp), Kombinerat (gemensam ledning)',
    backventil: 'En backventil förhindrar att vatten strömmar tillbaka från avloppssystemet',
    lagstaNiva: 'Den lägsta nivån där vatten kan tränga in i byggnaden vid översvämning',
    golvbrunnToalettHandfat: 'Finns det golvbrunn, toalett eller handfat i källaren som kan bli påverkat vid översvämning?',
    email: 'Ange din e-postadress om du vill bli kontaktad angående dina svar (valfritt)'
  };

  const InfoIcon: React.FC<{ field: keyof FormData }> = ({ field }) => (
    <span 
      style={{ 
        marginLeft: '8px', 
        cursor: 'pointer', 
        color: '#007bff',
        fontSize: '14px',
        fontWeight: 'bold',
        position: 'relative',
        display: 'inline-block'
      }}
      onClick={(e) => {
        e.stopPropagation();
        setActiveTooltip(activeTooltip === field ? null : field);
      }}
    >
      ℹ️
      {activeTooltip === field && (
        <div 
          style={{
            position: 'absolute',
            top: '25px',
            left: '0',
            backgroundColor: '#333',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '4px',
            fontSize: '12px',
            width: '250px',
            zIndex: 1000,
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {helpTexts[field]}
          <div style={{
            position: 'absolute',
            top: '-5px',
            left: '10px',
            width: '0',
            height: '0',
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderBottom: '5px solid #333'
          }} />
        </div>
      )}
    </span>
  );

  useEffect(() => {
    const handleClickOutside = () => {
      setActiveTooltip(null);
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchMunicipalities = async () => {
      setLoadingMunicipalities(true);
      try {
        const response = await fetch('https://data.opendatasoft.com/api/explore/v2.1/catalog/datasets/georef-sweden-kommun@public/records?select=kom_name&order_by=kom_name&limit=100');
        const data = await response.json();
        const municipalityNames = data.results.map((record: any) => record.kom_name);
        municipalityNames.sort((a: string, b: string) => a.localeCompare(b, 'sv-SE'));
        setMunicipalities(municipalityNames);
      } catch (error) {
        console.error('Error fetching municipalities from API, using local list:', error);
        setMunicipalities(swedishMunicipalities);
      } finally {
        setLoadingMunicipalities(false);
      }
    };

    fetchMunicipalities();
  }, []);

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};
    
    if (!formData.gatuadress) {
      newErrors.gatuadress = 'Detta fält är obligatoriskt';
    }
    if (!formData.kommun) {
      newErrors.kommun = 'Detta fält är obligatoriskt';
    }
    if (!formData.husetHarKallare) {
      newErrors.husetHarKallare = 'Detta fält är obligatoriskt';
    }
    if (!formData.vaAnslutning) {
      newErrors.vaAnslutning = 'Detta fält är obligatoriskt';
    }
    if (!formData.backventil) {
      newErrors.backventil = 'Detta fält är obligatoriskt';
    }
    if (!formData.lagstaNiva) {
      newErrors.lagstaNiva = 'Detta fält är obligatoriskt';
    }
    if (!formData.golvbrunnToalettHandfat) {
      newErrors.golvbrunnToalettHandfat = 'Detta fält är obligatoriskt';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    const submissionData = {
      code,
      streetAddress: formData.gatuadress,
      municipality: formData.kommun,
      propertyDesignation: formData.fastighetsbeteckning,
      hasBasement: formData.husetHarKallare,
      wasteWaterConnection: formData.vaAnslutning,
      backvalve: formData.backventil,
      lowestLevel: formData.lagstaNiva,
      basementFacilities: formData.golvbrunnToalettHandfat,
      email: formData.email || undefined,
      submittedAt: new Date().toISOString()
    };
    
    try {
      // Double check credentials encoding
      const username = 'apan';
      const password = 'banan';
      const credentials = btoa(`${username}:${password}`);
      console.log('Username:', username);
      console.log('Password:', password);
      console.log('Encoded credentials:', credentials);
      console.log('Should be: YXBhbjpiYW5hbg==');
      console.log('Submission data:', submissionData);
      
      const response = await fetch('https://arkady.deplide.org/dasaklim/form/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${credentials}`,
        },
        body: JSON.stringify(submissionData)
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      // If we get a 200 response, consider it successful
      if (response.status === 200) {
        console.log('Form submitted successfully with status 200');
        navigate('/success');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Try to parse response for other success codes
      let result;
      try {
        result = await response.json();
      } catch (jsonError) {
        result = await response.text();
      }
      
      console.log('Form submitted successfully:', result);
      navigate('/success');
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Ett fel uppstod när formuläret skulle skickas in. Försök igen.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!code || code.length !== 8 || !/^[a-zA-Z0-9]{8}$/.test(code)) {
    return (
      <div style={{ textAlign: 'center', marginTop: '50px' }}>
        <h2>Ogiltig kod</h2>
        <p>Du behöver en giltig 8-ställig kod för att komma åt detta formulär.</p>
        <button onClick={() => navigate('/')}>Tillbaka till startsidan</button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h1>DaSaKlim Formulär</h1>
        <div>
          <span style={{ fontSize: '14px', color: '#666' }}>Kod: {code}</span>
          <button 
            onClick={() => navigate('/')} 
            style={{ marginLeft: '20px', background: '#6c757d' }}
          >
            Tillbaka
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Gatuadress:<InfoIcon field="gatuadress" /></label>
          <input
            type="text"
            value={formData.gatuadress}
            onChange={(e) => handleInputChange('gatuadress', e.target.value)}
            placeholder="Ange gatuadress"
          />
          {errors.gatuadress && <div className="error">{errors.gatuadress}</div>}
        </div>

        <div className="form-group">
          <label>Kommun:<InfoIcon field="kommun" /></label>
          <select
            value={formData.kommun}
            onChange={(e) => handleInputChange('kommun', e.target.value)}
            disabled={loadingMunicipalities}
          >
            <option value="">
              {loadingMunicipalities ? 'Laddar kommuner...' : 'Välj kommun'}
            </option>
            {municipalities.map((municipality) => (
              <option key={municipality} value={municipality}>
                {municipality}
              </option>
            ))}
          </select>
          {errors.kommun && <div className="error">{errors.kommun}</div>}
        </div>

        <div className="form-group">
          <label>Fastighetsbeteckning:<InfoIcon field="fastighetsbeteckning" /></label>
          <input
            type="text"
            value={formData.fastighetsbeteckning}
            onChange={(e) => handleInputChange('fastighetsbeteckning', e.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Huset har källare:<InfoIcon field="husetHarKallare" /></label>
          <select
            value={formData.husetHarKallare}
            onChange={(e) => handleInputChange('husetHarKallare', e.target.value)}
          >
            <option value="">Välj alternativ</option>
            <option value="Ingen">Ingen</option>
            <option value="Oinredd">Oinredd</option>
            <option value="Inredd">Inredd</option>
          </select>
          {errors.husetHarKallare && <div className="error">{errors.husetHarKallare}</div>}
        </div>

        <div className="form-group">
          <label>VA-anslutning:<InfoIcon field="vaAnslutning" /></label>
          <select
            value={formData.vaAnslutning}
            onChange={(e) => handleInputChange('vaAnslutning', e.target.value)}
          >
            <option value="">Välj alternativ</option>
            <option value="Enskilt">Enskilt</option>
            <option value="Separat">Separat</option>
            <option value="Kombinerat">Kombinerat</option>
          </select>
          {errors.vaAnslutning && <div className="error">{errors.vaAnslutning}</div>}
        </div>

        <div className="form-group">
          <label>Backventil:<InfoIcon field="backventil" /></label>
          <select
            value={formData.backventil}
            onChange={(e) => handleInputChange('backventil', e.target.value)}
          >
            <option value="">Välj alternativ</option>
            <option value="Ja">Ja</option>
            <option value="Nej">Nej</option>
          </select>
          {errors.backventil && <div className="error">{errors.backventil}</div>}
        </div>

        <div className="form-group">
          <label>Lägsta nivå för inträngande vatten:<InfoIcon field="lagstaNiva" /></label>
          <select
            value={formData.lagstaNiva}
            onChange={(e) => handleInputChange('lagstaNiva', e.target.value)}
          >
            <option value="">Välj alternativ</option>
            <option value="> 1 trappsteg">&gt; 1 trappsteg</option>
            <option value="Krypgrund">Krypgrund</option>
            <option value="Marknivå">Marknivå</option>
          </select>
          {errors.lagstaNiva && <div className="error">{errors.lagstaNiva}</div>}
        </div>

        <div className="form-group">
          <label>Finns det något av följande i källaren: Golvbrunn, toalett, handfat:<InfoIcon field="golvbrunnToalettHandfat" /></label>
          <select
            value={formData.golvbrunnToalettHandfat}
            onChange={(e) => handleInputChange('golvbrunnToalettHandfat', e.target.value)}
          >
            <option value="">Välj alternativ</option>
            <option value="Ja">Ja</option>
            <option value="Nej">Nej</option>
          </select>
          {errors.golvbrunnToalettHandfat && <div className="error">{errors.golvbrunnToalettHandfat}</div>}
        </div>

        <div className="form-group">
          <label>E-post (valfritt):<InfoIcon field="email" /></label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            placeholder="din@epost.se"
          />
          <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
            Detta fält är valfritt. Ange endast om du vill bli kontaktad.
          </small>
        </div>

        <div style={{ 
          backgroundColor: '#f8f9fa', 
          padding: '15px', 
          borderRadius: '4px', 
          marginTop: '20px', 
          marginBottom: '20px',
          fontSize: '12px',
          color: '#666',
          lineHeight: '1.4'
        }}>
          <strong>GDPR-meddelande:</strong> Genom att skicka in detta formulär godkänner du att RISE Svenska Forskningsinstitutet sparar och använder den personliga information som du har lämnat för forsknings- och utvecklingsuppdrag.
        </div>

        <div style={{ marginTop: '30px' }}>
          <button 
            type="submit" 
            disabled={isSubmitting}
            style={{ width: '100%', padding: '15px', fontSize: '16px' }}
          >
            {isSubmitting ? 'Skickar...' : 'Skicka svar'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FormPage;